import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import { FileText, Sparkle, MagicWand, DownloadSimple } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { SectionEditor, DocBanner, getChantierSummary } from '../DocumentShared';

const DOC_TYPES = [
  { value: 'pgc', label: 'PGC', desc: 'Plan General de Coordination' },
  { value: 'ppsps', label: 'PPSPS', desc: 'Plan de Prevention' },
  { value: 'diuo', label: 'DIUO', desc: 'Dossier Interventions Ulterieures' },
  { value: 'icp', label: 'ICP', desc: 'Inspection Commune Prealable' },
];

export function DocumentWizard({
  open, onOpenChange, chantiers,
  wizardStep, setWizardStep,
  wizardType, setWizardType,
  wizardChantier, setWizardChantier,
  wizardEntreprise, setWizardEntreprise,
  wizardNom, setWizardNom,
  wizardVersion, setWizardVersion,
  wizardSummary, wizardSections, setWizardSections,
  generating, enriching, submitting,
  onGenerateTemplate, onEnrichAll, onSave,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" aria-describedby="wizard-desc">
        <SheetHeader>
          <SheetTitle>{wizardStep === 1 ? 'Générer un document CSPS' : 'Edition du document'}</SheetTitle>
          <p id="wizard-desc" className="text-sm text-[#4B5563]">
            {wizardStep === 1 ? 'Sélectionnez le type et le chantier' : 'Modifiéz chaque section individuellement'}
          </p>
        </SheetHeader>

        {wizardStep === 1 && (
          <div className="space-y-5 mt-6">
            {/* Type selection */}
            <div>
              <Label className="text-sm font-semibold">Type de document</Label>
              <div className="space-y-2 mt-2">
                {DOC_TYPES.map(t => {
                  const selected = wizardType === t.value;
                  return (
                    <button key={t.value} type="button"
                      onClick={() => { setWizardType(t.value); setWizardEntreprise(''); }}
                      className={`w-full flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 transition-all text-left ${
                        selected ? 'border-[#FF6B00] bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`wizard-type-${t.value}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selected ? 'bg-[#FF6B00] text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <FileText size={22} />
                      </div>
                      <div className="min-w-0">
                        <p className={`font-semibold text-sm ${selected ? 'text-[#FF6B00]' : 'text-[#111827]'}`}>{t.label}</p>
                        <p className="text-xs text-[#4B5563] mt-0.5">{t.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Chantier selector */}
            <div>
              <Label className="text-sm font-semibold">Chantier CSPS</Label>
              <Select value={wizardChantier} onValueChange={setWizardChantier}>
                <SelectTrigger className="mt-1 h-14" data-testid="wizard-chantier-select">
                  <SelectValue placeholder="Sélectionner un chantier" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]">
                  {(chantiers || []).filter(c => c && c.id && c.name).map(c => (
                    <SelectItem key={c.id} value={String(c.id)} className="py-3">{c.name} ({c.address || ''})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Chantier preview */}
            {wizardChantier && (() => {
              const c = chantiers.find(ch => ch.id === wizardChantier);
              return c ? (
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-sm font-semibold text-orange-800 mb-2">Donnees auto-remplies</p>
                    <div className="grid grid-cols-2 gap-1.5 text-xs text-orange-700">
                      <span>MO : {c.client_name}</span>
                      <span>MOE : {c.maitre_oeuvre || '-'}</span>
                      <span>CSPS : {c.coordonnateur_sps || '-'}</span>
                      <span>Niveau : {c.niveau_csps}</span>
                      <span>Phase : {c.phase_chantier}</span>
                      {c.nature_travaux && <span>Nature : {c.nature_travaux}</span>}
                      {c.duree_previsionnelle && <span>Duree : {c.duree_previsionnelle}</span>}
                      {c.numéro_operation && <span>N. op. : {c.numéro_operation}</span>}
                      {c.reference_dossier && <span>Ref. : {c.reference_dossier}</span>}
                      <span className="col-span-2">Entreprises : {(c.entreprises || []).join(', ') || 'Aucune'}</span>
                      {c.risques_principaux && <span className="col-span-2">Risques : {c.risques_principaux.substring(0, 60)}...</span>}
                    </div>
                  </CardContent>
                </Card>
              ) : null;
            })()}

            {/* ICP: Enterprise selector */}
            {wizardType === 'icp' && wizardChantier && (() => {
              const c = chantiers.find(ch => ch.id === wizardChantier);
              const entreprises = c?.entreprises || [];
              return (
                <div data-testid="wizard-icp-entreprise">
                  <Label className="text-sm font-semibold">Entreprise inspectee</Label>
                  {entreprises.length > 0 ? (
                    <Select value={wizardEntreprise} onValueChange={setWizardEntreprise}>
                      <SelectTrigger className="mt-1 h-14" data-testid="wizard-icp-entreprise-select">
                        <SelectValue placeholder="Sélectionner l'entreprise" />
                      </SelectTrigger>
                      <SelectContent position="popper" className="z-[100]">
                        {entreprises.map(e => <SelectItem key={e} value={e} className="py-3">{e}</SelectItem>)}
                        <SelectItem value="__autre__" className="py-3 text-gray-500">Autre entreprise...</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : null}
                  {(entreprises.length === 0 || wizardEntreprise === '__autre__') && (
                    <Input
                      value={wizardEntreprise === '__autre__' ? '' : wizardEntreprise}
                      onChange={(e) => setWizardEntreprise(e.target.value)}
                      placeholder="Nom de l'entreprise"
                      className="mt-1 h-14" data-testid="wizard-icp-entreprise-input" />
                  )}
                </div>
              );
            })()}

            <Button onClick={onGenerateTemplate} disabled={generating || !wizardChantier}
              className="w-full h-14 text-base bg-[#FF6B00] hover:bg-orange-700" data-testid="wizard-generate-btn">
              {generating ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <MagicWand size={20} className="mr-2" />}
              Générer le template
            </Button>
          </div>
        )}

        {wizardStep === 2 && (
          <div className="space-y-4 mt-4">
            <DocBanner name={wizardNom} chantierName={(chantiers || []).find(c => c && c.id === wizardChantier)?.name || ''}
              version={wizardVersion} status="brouillon" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nom du document</Label>
                <Input value={wizardNom} onChange={(e) => setWizardNom(e.target.value)}
                  className="mt-1 h-10 text-sm" data-testid="wizard-nom" />
              </div>
              <div>
                <Label className="text-xs">Version</Label>
                <Input value={wizardVersion} onChange={(e) => setWizardVersion(e.target.value)}
                  className="mt-1 h-10 text-sm" data-testid="wizard-version" />
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEnrichAll} disabled={enriching}
                className="text-purple-600 border-purple-200 hover:bg-purple-50" data-testid="wizard-enrich-btn">
                {enriching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkle size={16} className="mr-1" />}
                {enriching ? 'Traitement...' : 'Completer tout'}
              </Button>
            </div>

            <SectionEditor
              sections={wizardSections}
              setSections={setWizardSections}
              chantierId={wizardChantier}
              typeDoc={wizardType}
              summary={wizardSummary}
            />

            <div className="sticky bottom-0 bg-white py-3 border-t flex gap-3">
              <Button variant="outline" onClick={() => setWizardStep(1)} className="flex-1 h-12">Retour</Button>
              <Button onClick={onSave} disabled={submitting}
                className="flex-1 h-12 bg-[#FF6B00] hover:bg-orange-700 text-base" data-testid="wizard-save-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadSimple size={20} className="mr-2" />}
                Enregistrér
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
